/* eslint-disable max-lines */
import React from 'react';
import _ from 'lodash';
import throttleWithRAF from './../kit/throttleWithRAF';
import {
  SpreadsheetGridContainer,
  SpreadsheetGridScroll,
  SpreadsheetGridHeader,
  SpreadsheetGridHeadCell,
  SpreadsheetGridResizer,
} from '../../styles';
import Grid from '../grid';
import ScrollDummy from './../scrollDummy';
import { CommonProps } from '../kit/interface';
import { DEFAULT_WIDTH_Q3_COLUMNS } from 'utils/constants';

const RESERVE_ROWS_COUNT = 3;

interface Props extends CommonProps {
  active: boolean;

  // scroll
  isScrollable: boolean;
  onScroll?: (value: number) => void;
  onScrollReachesBottom?: () => void;
  // resize
  isColumnsResizable: boolean;
  onColumnResize?: (widthValues: { [key in number | string]: number }) => void;

  onCellMove?: ({ x, y }: { x: number; y: number }) => void;
}

interface State {
  first: number;
  last: number;
  offset: number;
  columnWidthValues: { [key in string | number]: number };
  hasScroll?: boolean;
}

class SpreadsheetGridScrollWrapper extends React.PureComponent<Props, State> {
  static defaultProps = {
    rows: [],
    isColumnsResizable: false,
    placeholder: 'There are no rows',
    headerHeight: 40,
    rowHeight: 58,
    isScrollable: true,
    focusOnSingleClick: false,
  };

  tableEl: React.RefObject<any>;
  scrollDummyEl: React.RefObject<any>;
  scrollWrapperEl: React.RefObject<any>;
  grid: React.RefObject<any>;

  resizingCell: any;
  nextResizingCell: any;
  currentCoords: any;

  constructor(props: Props) {
    super(props);

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.setScrollState = this.setScrollState.bind(this);
    this.startColumnResize = this.startColumnResize.bind(this);
    this.processColumnResize = this.processColumnResize.bind(this);

    this.tableEl = React.createRef();
    this.scrollDummyEl = React.createRef();
    this.scrollWrapperEl = React.createRef();
    this.grid = React.createRef();

    this.resizingCell = null;
    this.nextResizingCell = null;
    this.currentCoords = null;

    this.state = {
      first: 0,
      last: this.calculateInitialLast(),
      offset: 0,
      columnWidthValues: {},
    };

    // if requestAnimationFrame is available, use it to throttle refreshState
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      this.setScrollState = throttleWithRAF(this.setScrollState);
    }
  }

  componentDidMount() {
    this.freezeTable();

    if (this.props.isColumnsResizable) {
      document.addEventListener('mousemove', this.processColumnResize, false);
      document.addEventListener(
        'mouseup',
        () => {
          this.resizingCell = null;
        },
        false,
      );
    }

    window.addEventListener('resize', this.onResize, false);

    this.setScrollState();
  }

  componentDidUpdate(prevProps: Props) {
    const { columns, rows } = this.props;

    // If columns has been changed, recalculate their width values.
    if (prevProps.columns !== columns) {
      this.freezeTable();
    }
    if (rows !== prevProps.rows) {
      this.setScrollState();
    }
  }

  componentWillUnmount() {
    if (this.props.isColumnsResizable) {
      document.removeEventListener('mousemove', this.processColumnResize, false);
      document.removeEventListener(
        'mouseup',
        () => {
          this.resizingCell = null;
        },
        false,
      );
    }

    window.removeEventListener('resize', this.onResize, false);
  }

  resetScroll() {
    this.scrollWrapperEl.current.scrollTop = 0;
    this.setScrollState();
  }

  focusCell(nextFocusedCell: any) {
    this.grid.current.focusCell(nextFocusedCell);
  }

  freezeTable() {
    const table = this.tableEl.current;

    if (table) {
      const { columns } = this.props;

      const preparedColumnWidthValues: { [key: string]: number } = {};

      columns.forEach((column) => {
        const width = column.width || DEFAULT_WIDTH_Q3_COLUMNS;
        preparedColumnWidthValues[column.id] = width;
      });

      this.setState({
        columnWidthValues: preparedColumnWidthValues,
      });
    }
  }

  startColumnResize(e: { currentTarget: { offsetParent: any }; pageX: number }) {
    this.resizingCell = e.currentTarget.offsetParent;
    this.resizingCell.startOffset = e.pageX;
    this.resizingCell.startWidth = this.resizingCell.offsetWidth;
  }

  processColumnResize(e: { pageX: number }) {
    if (!this.resizingCell) return;

    const delta = e.pageX - this.resizingCell.startOffset;
    let newWidth = this.resizingCell.startWidth + delta;

    const MIN_WIDTH = 50;
    const MAX_WIDTH = 200;
    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

    const columnIndex = this.resizingCell.dataset.index;
    const columnId = this.props.columns[columnIndex].id;

    const columnWidthValues = { ...this.state.columnWidthValues };
    columnWidthValues[columnId] = newWidth;

    this.setState({ columnWidthValues });

    if (this.props.onColumnResize) {
      this.props.onColumnResize(columnWidthValues);
    }
  }

  calculateInitialLast() {
    return this.props.isScrollable
      ? Math.ceil(window.outerHeight / this.props.rowHeight) + RESERVE_ROWS_COUNT
      : this.props.rows.length;
  }

  calculateLast(first: number) {
    const wrapper = this.scrollWrapperEl.current;
    const visibleHeight = wrapper ? wrapper.parentNode.offsetHeight + 200 : 0;

    return first + Math.ceil(visibleHeight / this.props.rowHeight);
  }

  setScrollState() {
    const scrollWrapperEl = this.scrollWrapperEl.current;
    const scrollDummyEl = this.scrollDummyEl.current;
    const rows = this.props.rows;

    if (!this.props.isScrollable) {
      return;
    }

    const scrollTop = Math.max(scrollWrapperEl?.scrollTop, 0);
    const first = Math.max(0, Math.floor(scrollTop / this.props.rowHeight) - RESERVE_ROWS_COUNT);
    const last = Math.min(rows.length, this.calculateLast(first) + RESERVE_ROWS_COUNT);
    let newState = {
      hasScroll:
        scrollWrapperEl?.scrollHeight > scrollWrapperEl?.offsetHeight &&
        // Check if the scroll has a width
        scrollWrapperEl.offsetWidth > scrollDummyEl.offsetWidth,
    };

    if (first !== this.state.first || last !== this.state.last) {
      newState = {
        ...newState,
        ...{
          first,
          last,
          offset: first * this.props.rowHeight,
        },
      };
    }
    this.setState(newState);

    if (this.props.onScroll) {
      this.props.onScroll(scrollTop);
    }

    if (
      this.props.onScrollReachesBottom &&
      scrollWrapperEl.offsetHeight + scrollWrapperEl.scrollTop >= scrollWrapperEl.scrollHeight
    ) {
      this.props.onScrollReachesBottom();
    }
  }

  onResize() {
    this.setScrollState();
  }

  onScroll() {
    this.setScrollState();
  }

  getDisabledCells(rows: Props['rows'], startIndex: number) {
    const disabledCells: any[] = [];
    const disabledCellChecker = this.props.disabledCellChecker;

    if (disabledCellChecker) {
      rows.forEach((row, x) => {
        this.props.columns.forEach((column, y) => {
          if (disabledCellChecker(row, column.id)) {
            disabledCells.push({ x: startIndex + x, y });
          }
        });
      });
    }

    return disabledCells;
  }

  renderResizer() {
    return (
      <SpreadsheetGridResizer
        onMouseDown={this.startColumnResize}
        style={{
          height: this.props.headerHeight + 'px',
        }}
      />
    );
  }

  renderHeader() {
    const columns = this.props.columns;
    const { columnWidthValues } = this.state;

    return (
      <SpreadsheetGridHeader scrollable={this.state.hasScroll || false}>
        {columns.map((column, i) => {
          // By default, resize is enabled; we disable it only if isResizable === false
          const canResize = this.props.isColumnsResizable && column.isResizable !== false;
          return (
            <SpreadsheetGridHeadCell
              key={i}
              className="SpreadsheetGrid__headCell"
              data-index={i}
              style={{
                height: this.props.headerHeight + 'px',
                width: columnWidthValues ? columnWidthValues[columns[i].id] + 'px' : 'auto',
              }}
            >
              {typeof column.title === 'string' ? column.title : column.title()}
              {canResize && i !== columns.length - 1 && this.renderResizer()}
            </SpreadsheetGridHeadCell>
          );
        })}
      </SpreadsheetGridHeader>
    );
  }

  render() {
    const rows = _.slice(this.props.rows, this.state.first, this.state.last);

    return (
      <SpreadsheetGridContainer className="SpreadsheetGridContainer" ref={this.tableEl}>
        {this.renderHeader()}
        <SpreadsheetGridScroll
          scrollable={this.props.isScrollable}
          headerHeight={this.props.headerHeight}
          onScroll={this.onScroll}
          ref={this.scrollWrapperEl}
        >
          <ScrollDummy
            rows={this.props.rows}
            headerHeight={this.props.headerHeight}
            rowHeight={this.props.rowHeight}
            refEl={this.scrollDummyEl}
          />
          {
            <Grid
              {...this.props}
              ref={this.grid}
              rows={rows}
              allRows={this.props.rows}
              rowsCount={this.props.rows.length}
              startIndex={this.state.first}
              offset={this.state.offset}
              columnWidthValues={this.state.columnWidthValues}
              onCellMove={this.props.onCellMove}
              disabledCells={this.getDisabledCells(rows, this.state.first)}
            />
          }
        </SpreadsheetGridScroll>
      </SpreadsheetGridContainer>
    );
  }
}

export default SpreadsheetGridScrollWrapper;
